import { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router";
import { useForm, SubmitHandler } from 'react-hook-form';
import useSignIn from '../../hooks/Auth/useSignIn';
import useResendConfirmationWithToken from '../../hooks/Auth/useResendConfirmationWithToken';
import { readStoredToken } from '../../apollo/client';
import { EyeCloseIcon, EyeIcon } from "../../icons";
import { useStoreSelector } from '../../hooks/useStoreFromContext';
import { AuthContext } from "../../context/AuthContext.tsx";
import { useToast } from "../../context/ToastContext";
import Label from "../form/Label";
import Input from "../form/input/InputField";
import Button from "../ui/button/Button";

import type { AppState } from '../../store';
import type { SignInFormValues } from '../../types/signInTypes';

type SignInError = {
  cause?: {
    notConfirmed?: boolean;
  };
};

const SignInForm = () => {
  const { register, handleSubmit } = useForm<SignInFormValues>();
  const toast = useToast();
  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const [showPassword, setShowPassword] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [email, setEmail] = useState('');
  // At the moment this action (notConfirmed) does not work 
  //   because it's disabled on server
  const [notConfirmed, setNotConfirmed] = useState(false);
  const { resendConfirmationWithToken } = useResendConfirmationWithToken({
    onCompleted: ({alreadySent, message, success}) => {
      if (success) {
        if (alreadySent) return toast.warning(message)
        return toast.success(message)
      }
    },
    onError: (err) => {
      if (err) return toast.error(String(err)); 
    }, 
    mutationOptions: { awaitRefetchQueries: true }
  });

  const { signIn } = useSignIn({
    onCompleted: (user) => {
      if (user) {
        setUser(user);
        const storedToken = readStoredToken();
        if (storedToken?.['access-token']) {
          auth?.login();
          setShouldRedirect(true);
        }
      }
    },
    onError: (err: unknown) => {
      const error = err as SignInError;
      if (error.cause?.notConfirmed) {
        setNotConfirmed(error.cause.notConfirmed)
        return toast.warning(String(err))
      }
      if (err) return toast.error(String(err)); 
    }, 
    mutationOptions: { awaitRefetchQueries: true }
  });
  const setUser = useStoreSelector((s: AppState) => s.setUser);

  useEffect(() => {
    if (shouldRedirect) {
      navigate('/dashboard', { replace: true }); 
    }
  }, [shouldRedirect, navigate]);

  const onSubmit: SubmitHandler<SignInFormValues> = async formValues => {
    setEmail(formValues.email)
    await signIn({
      email: formValues.email,
      password: formValues.password
    });
  };

  const resendConfirmation = async () => {
    await resendConfirmationWithToken({
      email: email
    });
  }

  return (
    <div className="flex flex-col flex-1">
      <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
        <div>
          <div className="mb-5 sm:mb-8">
            <h1 className="mb-2 font-semibold text-gray-800 text-title-sm dark:text-white/90 sm:text-title-md">
              Sign In
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Enter your email and password to sign in!
            </p>
          </div>
          <div>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="space-y-6">
                <div>
                  <Label>
                    Email <span className="text-error-500">*</span>{" "}
                  </Label>
                  <Input 
                    placeholder="info@gmail.com" 
                    {...register("email")}
                  />
                  {email && notConfirmed && 
                    <div 
                      className="mt-2 cursor-pointer text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400"
                      onClick={resendConfirmation}
                    >
                    {`Resend confirmation to email: ${email}`}
                  </div>}
                </div>
                <div>
                  <Label>
                    Password <span className="text-error-500">*</span>{" "}
                  </Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      {...register("password")}
                    />
                    <span
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute z-30 -translate-y-1/2 cursor-pointer right-4 top-1/2"
                    >
                      {showPassword ? (
                        <EyeIcon className="fill-gray-500 dark:fill-gray-400 size-5" />
                      ) : (
                        <EyeCloseIcon className="fill-gray-500 dark:fill-gray-400 size-5" />
                      )}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  {/* <div className="flex items-center gap-3">
                    <Checkbox checked={isChecked} onChange={setIsChecked} />
                    <span className="block font-normal text-gray-700 text-theme-sm dark:text-gray-400">
                      Keep me logged in
                    </span>
                  </div> */}
                  <Link
                    to="/reset_password"
                    className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div>
                  <Button 
                    className="w-full"
                    size="md"
                  >
                    Sign In
                  </Button>
                </div>
              </div>
            </form>
            <div className="mt-5">
              <p className="text-sm font-normal text-center text-gray-700 dark:text-gray-400 sm:text-start">
                Don&apos;t have an account? {" "}
                <Link
                  to="/signup"
                  className="text-brand-500 hover:text-brand-600 dark:text-brand-400"
                >
                  Sign Up
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SignInForm;
